import React from 'react'
import {
  Stack,
  FileInput,
  Group,
  Box,
  Loader,
  Alert,
  Paper,
  Progress
} from '@mantine/core'
import { FiInfo, FiSearch, FiX, FiUpload } from 'react-icons/fi'
import AppModal from '../../app/common/ui/AppModal'
import AppButton from '../../app/common/ui/AppButton'
import AppCheckbox from '../../app/common/ui/AppCheckbox'
import AppInput from '../../app/common/ui/AppInput'
import AppSelect from '../../app/common/ui/AppSelect'
import AppText from '../../app/common/ui/AppText'
import { MAX_SLOP_VIDEO_BYTES } from './slopFeedConstants'
import type { SlopLibraryFeed } from './hooks/useSlopLibraryFeed'

type Props = Pick<
  SlopLibraryFeed,
  | 'uploadModalOpen'
  | 'closeUploadModal'
  | 'uploadError'
  | 'videoFile'
  | 'setVideoFile'
  | 'uploadTitle'
  | 'setUploadTitle'
  | 'uploadDescription'
  | 'setUploadDescription'
  | 'consentGiven'
  | 'setConsentGiven'
  | 'firearmModels'
  | 'cartridgeOptions'
  | 'firearmListLoading'
  | 'firearmSearch'
  | 'setFirearmSearch'
  | 'selectedFirearmModelId'
  | 'setSelectedFirearmModelId'
  | 'selectedCartridgeSpecId'
  | 'setSelectedCartridgeSpecId'
  | 'uploadLoading'
  | 'uploadProgress'
  | 'consentText'
  | 'consentLoading'
  | 'handleUpload'
>

export default function SlopUploadModal (props: Props) {
  const {
    uploadModalOpen,
    closeUploadModal,
    uploadError,
    videoFile,
    setVideoFile,
    uploadTitle,
    setUploadTitle,
    uploadDescription,
    setUploadDescription,
    consentGiven,
    setConsentGiven,
    firearmModels,
    cartridgeOptions,
    firearmListLoading,
    firearmSearch,
    setFirearmSearch,
    selectedFirearmModelId,
    setSelectedFirearmModelId,
    selectedCartridgeSpecId,
    setSelectedCartridgeSpecId,
    uploadLoading,
    uploadProgress,
    consentText,
    consentLoading,
    handleUpload
  } = props

  const selectedModel = firearmModels.find((m) => m.id === selectedFirearmModelId)

  return (
    <AppModal
      opened={uploadModalOpen}
      onClose={closeUploadModal}
      title="Upload to Slop Feed"
      size="lg"
    >
      <Stack gap="md">
        {uploadError && (
          <Alert icon={<FiInfo />} title="Error" color="red">
            {uploadError}
          </Alert>
        )}

        <FileInput
          label="Video File"
          placeholder="Select MP4, MOV, or WebM (max 150 MB)"
          accept="video/mp4,video/quicktime,video/webm"
          value={videoFile}
          onChange={setVideoFile}
          disabled={uploadLoading}
        />

        <AppInput
          label="Title"
          placeholder="What are you shooting?"
          value={uploadTitle}
          onChange={(e) => setUploadTitle(e.currentTarget.value)}
          disabled={uploadLoading}
        />

        <AppInput
          label="Description (optional)"
          placeholder="Details about the setup, specs, etc."
          value={uploadDescription}
          onChange={(e) => setUploadDescription(e.currentTarget.value)}
          multiline
          minRows={3}
          disabled={uploadLoading}
        />

        <Box>
          <AppText size="sm" fw={500} mb={6}>Link a Firearm (optional)</AppText>
          {firearmListLoading
            ? <Loader size="sm" />
            : (
            <>
              {selectedFirearmModelId
                ? (
                <Group spacing={6} mb={6}>
                  <AppText size="sm" c="dimmed">
                    {selectedModel ? `${selectedModel.make} ${selectedModel.model}` : selectedFirearmModelId}
                  </AppText>
                  <AppButton
                    variant="subtle"
                    size="xs"
                    compact
                    leftIcon={<FiX size={12} />}
                    onClick={() => { setSelectedFirearmModelId(null); setFirearmSearch('') }}
                    disabled={uploadLoading}
                  >
                    Clear
                  </AppButton>
                </Group>
                  )
                : (
                <>
                  <AppInput
                    placeholder="Search make, model, or caliber…"
                    icon={<FiSearch size={14} />}
                    value={firearmSearch}
                    onChange={(e) => setFirearmSearch(e.currentTarget.value)}
                    disabled={uploadLoading}
                    mb={4}
                  />
                  {firearmSearch.trim().length > 0 && (
                    <Stack spacing={4}>
                      {firearmModels
                        .filter((m) =>
                          `${m.make} ${m.model} ${m.caliber}`.toLowerCase().includes(firearmSearch.toLowerCase())
                        )
                        .slice(0, 6)
                        .map((m) => (
                          <Paper
                            key={m.id}
                            p={6}
                            withBorder
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'var(--gs-surface3)' } }}
                            onClick={() => { setSelectedFirearmModelId(m.id); setFirearmSearch('') }}
                          >
                            <AppText size="sm">{m.make} {m.model}</AppText>
                            <AppText size="xs" c="dimmed">{m.caliber}</AppText>
                          </Paper>
                        ))}
                    </Stack>
                  )}
                </>
                  )}
            </>
              )}
        </Box>

        <AppSelect
          label="Ammunition / Cartridge (optional)"
          placeholder="Select cartridge…"
          data={cartridgeOptions}
          value={selectedCartridgeSpecId}
          onChange={(val) => setSelectedCartridgeSpecId(val)}
          disabled={uploadLoading || firearmListLoading}
          clearable
          searchable
        />

        {videoFile && videoFile.size > MAX_SLOP_VIDEO_BYTES && (
          <Alert icon={<FiInfo />} title="File Too Large" color="red">
            Video exceeds 150 MB limit.
          </Alert>
        )}

        <Box
          sx={{
            background: 'var(--gs-surface2)',
            border: '1px solid var(--gs-border)',
            borderRadius: 6,
            padding: 12
          }}
        >
          <AppText sx={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
            Consent Terms {consentText && `(v${consentText.version})`}
          </AppText>
          {consentLoading
            ? <Loader size="sm" />
            : consentText
              ? (
              <>
                <AppText sx={{ fontSize: 11, color: 'var(--gs-muted)', whiteSpace: 'pre-wrap', lineHeight: 1.6, mb: 12 }}>
                  {consentText.body}
                </AppText>
                <AppCheckbox
                  label="I have read and agree to the above terms"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.currentTarget.checked)}
                  disabled={uploadLoading}
                />
              </>
                )
              : (
              <AppText sx={{ fontSize: 11, color: 'var(--gs-muted)' }}>
                Unable to load consent terms. Please try again later.
              </AppText>
                )}
        </Box>

        {uploadLoading && uploadProgress > 0 && (
          <Progress value={uploadProgress} label={`${uploadProgress}%`} />
        )}

        <Group position="right">
          <AppButton variant="default" onClick={closeUploadModal} disabled={uploadLoading}>
            Cancel
          </AppButton>
          <AppButton
            onClick={() => void handleUpload()}
            disabled={
              !videoFile ||
              !uploadTitle.trim() ||
              !consentGiven ||
              uploadLoading ||
              (videoFile?.size ?? 0) > MAX_SLOP_VIDEO_BYTES
            }
            loading={uploadLoading}
            leftIcon={<FiUpload size={14} />}
          >
            {uploadLoading ? 'Uploading...' : 'Upload'}
          </AppButton>
        </Group>
      </Stack>
    </AppModal>
  )
}
